import { Suspense, useMemo } from "react";
import { styled } from "@mui/material";
import { baseUrl, fetchKmsData } from "@mediaspace/shared/utils";
import { ActionButtons } from "./actions/buttons";
import { KmsTypeHeroProps, KmsTypeMediaSchedulingData, StatisticsIndicatorType } from "@mediaspace/shared/types";
import { Box } from "@mediaspace/shared/styled";
import { Title } from "./title/Title";
import { KmsTypeComponent } from "@mediaspace/shared/types";
import { MediaTitleArea, MediaTitleAreaProps, MediaTitleActionsProps } from "./MediaTitleArea";
import { TitleSkeleton } from "./title-skeleton/TitleSkeleton";
import { ErrorBoundary } from "@mediaspace/shared/ui";

/**
 * Area below the player (or other media hero component) that contains:
 * - media title
 * - basic info (e.g. creation date)
 * - buttons (e.g. likes, comments)
 * - actions menu (e.g. edit, delete, publish)
 */

const StyledAreaContainer = styled(Box, {
    shouldForwardProp(propName: PropertyKey): boolean {
        return propName !== "isEmptyRightPanel";
    },
})<{ isEmptyRightPanel?: boolean }>(({ theme, isEmptyRightPanel }) => ({
    display: "flex",
    alignItems: "baseline",
    width: "100%",
    gap: theme.spacing(1, 3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down(isEmptyRightPanel ? theme.breakpoints.values.md : theme.breakpoints.values.lg)]: {
        flexDirection: "column",
    },
}));

const StyledTitleContainer = styled(Box)({
    flex: "1 0 0",
    minWidth: 0,
});

interface MediaTitleAreaContentProps extends Pick<MediaTitleAreaProps, "titleAlerts" | "isEmptyRightPanel"> {
    fetchData: () => MediaTitleActionsProps;
}

/**
 * Media title area
 */
const MediaTitleAreaContent = ({ fetchData, titleAlerts = [], isEmptyRightPanel }: MediaTitleAreaContentProps) => {
    const mediaTitleActionsProps = fetchData();

    return (
        <MediaTitleArea
            titleAlerts={titleAlerts}
            isEmptyRightPanel={isEmptyRightPanel}
            mediaTitleActionsProps={mediaTitleActionsProps}
        />
    );
};

export interface MediaTitleAreaContainerProps
    extends Pick<KmsTypeHeroProps, "categoryId" | "playlist" | "titleAlerts"> {
    entryId: string;
    isEmptyRightPanel: boolean;
}

/**
 * Media title area Suspense Container
 */
export const MediaTitleAreaContainer = ({
    entryId,
    categoryId,
    playlist,
    titleAlerts,
    isEmptyRightPanel = false,
}: MediaTitleAreaContainerProps) => {
    const url =
        baseUrl +
        "/default/media/entry-actions/id/" +
        entryId +
        (categoryId ? "/categoryid/" + categoryId : "") +
        (playlist ? "/playlistid/" + playlist.id : "");
    const fetchData = useMemo(() => fetchKmsData<MediaTitleActionsProps>(url), [url]);

    return (
        <ErrorBoundary fallback={<TitleSkeleton />}>
            <Suspense fallback={<TitleSkeleton />}>
                <MediaTitleAreaContent
                    fetchData={fetchData}
                    titleAlerts={titleAlerts}
                    isEmptyRightPanel={isEmptyRightPanel}
                />
            </Suspense>
        </ErrorBoundary>
    );
};
